import React, { ReactNode, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '4xl';
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  maxWidth = 'md',
}) => {
  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleEscape]);

  if (!isOpen) return null;

  const maxWidthClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '4xl': 'max-w-4xl',
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm select-none"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className={`${maxWidthClasses[maxWidth]} w-full select-auto`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body
  );
};

interface ModalContentProps {
  children: ReactNode;
  gradient?: boolean;
}

export const ModalContent: React.FC<ModalContentProps> = ({ children, gradient = false }) => {
  const { darkMode } = useTheme();

  const baseClasses = gradient
    ? darkMode
      ? 'bg-gradient-to-br from-purple-900 to-pink-900 border-violet-500/50'
      : 'bg-gradient-to-br from-purple-50 to-pink-50 border-slate-200'
    : darkMode
    ? 'bg-slate-900 border-white/10'
    : 'bg-white border-slate-200';

  return (
    <div className={`p-6 rounded-2xl border shadow-xl max-h-[calc(100dvh-2rem)] overflow-y-auto ${baseClasses}`}>
      {children}
    </div>
  );
};

interface ModalHeaderProps {
  title: string;
  icon?: ReactNode;
}

export const ModalHeader: React.FC<ModalHeaderProps> = ({ title, icon }) => {
  const { darkMode } = useTheme();

  return (
    <div className="text-center mb-4">
      {icon && <div className="mb-4">{icon}</div>}
      <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
        {title}
      </h3>
    </div>
  );
};

interface ModalFooterProps {
  children: ReactNode;
}

export const ModalFooter: React.FC<ModalFooterProps> = ({ children }) => {
  return <div className="flex gap-3 mt-6">{children}</div>;
};
